import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { formatNumber } from '@/lib/i18n/format'
import { currentLocale } from '@/lib/i18n'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'
import { LoadingState } from '@/components/loading-state'

import { chatApi, type Participant } from '../api'
import { chatErrorKey } from '../format'
import { ChatAvatar } from './chat-avatar'
import { ChatModal } from './chat-modal'

interface ChatMembersSheetProps {
  activityId: string
  open: boolean
  onClose: () => void
}

type MembersPhase = 'loading' | 'ready' | 'error'

/** Members sheet for the activity chat header (UI_PARITY §3.11). */
export function ChatMembersSheet({
  activityId,
  open,
  onClose,
}: ChatMembersSheetProps) {
  return (
    <ChatModal
      open={open}
      onClose={onClose}
      titleKey="chat.members.title"
      size="members"
    >
      {open ? (
        <MembersContent activityId={activityId} onClose={onClose} />
      ) : null}
    </ChatModal>
  )
}

function MembersContent({
  activityId,
  onClose,
}: {
  activityId: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  // D-101: activity participants who are not friends display as "Nome S.".
  const formatPerson = usePersonNameFormatter()
  const [participants, setParticipants] = useState<Participant[]>([])
  const [phase, setPhase] = useState<MembersPhase>('loading')
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    const controller = new AbortController()
    chatApi
      .listParticipants(activityId, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return
        setParticipants(response.items)
        setPhase('ready')
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setPhase('error')
      })
    return () => controller.abort()
  }, [activityId])

  return (
    <>
      {phase === 'loading' ? (
        <LoadingState compact labelKey="chat.members.loading" />
      ) : null}

      {phase === 'error' ? (
        <p role="alert" className="chat-members__error">
          {t(chatErrorKey(error))}
        </p>
      ) : null}

      {phase === 'ready' ? (
        <>
          <p className="chat-members__count">
            {t('chat.members.count', { count: participants.length })}
          </p>
          {participants.length === 0 ? (
            <p className="chat-members__empty">{t('chat.members.empty')}</p>
          ) : (
            <ul className="chat-members__list">
              {participants.map((participant) => {
                const rating =
                  participant.rating === null
                    ? t('chat.members.noRating')
                    : t('chat.members.rating', {
                        rating: formatNumber(
                          participant.rating,
                          currentLocale(),
                          { maximumFractionDigits: 1 },
                        ),
                        count: participant.ratingCount,
                      })
                return (
                  <li key={participant.id}>
                    <Link
                      to={`/users/${participant.id}`}
                      className="chat-members__row"
                      onClick={onClose}
                    >
                      <ChatAvatar
                        name={formatPerson(participant)}
                        src={participant.avatarUrl}
                        size="md"
                      />
                      <span className="chat-members__body">
                        <span className="chat-members__name">
                          {formatPerson(participant)}
                        </span>
                        <span className="chat-members__meta">
                          {[participant.city, rating]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      ) : null}
    </>
  )
}
